/**
 * buildAdvisorContext
 * ──────────────────
 * Shared context builder for both the home-page AI Executive Advisor
 * and the GEM chat page. Fetches 4 APIs in parallel, fixes owner name
 * extraction, and returns a structured context string for GPT.
 */

import GetPerformanceStatsApi   from '../api/performance/GetPerformanceStatsApi';
import GetBestWorstTopPlans      from '../api/performance/GetBestWorstTopPlans';
import GetEmployeeSummaryApi     from '../api/performance/GetEmployeeSummaryApi';
import GetAttentionRequiredApi   from '../api/plans/GetAttentionRequiredApi';

// ─── helpers ──────────────────────────────────────────────────────────────────

function pct(n) {
  return `${Number(n || 0).toFixed(1)}%`;
}

function ownerName(assignedTo) {
  if (!assignedTo) return 'Unassigned';
  if (typeof assignedTo === 'string') return assignedTo;
  // object shape: { firstName, lastName, ... }
  const first = assignedTo.firstName || '';
  const last  = assignedTo.lastName  || '';
  return `${first} ${last}`.trim() || 'Unassigned';
}

function daysLeft(endDate) {
  if (!endDate) return null;
  const diff = Math.ceil((new Date(endDate) - new Date()) / 86400000);
  return diff > 0 ? diff : 0;
}

function timeElapsedPct(start, end) {
  if (!start || !end) return 0;
  const total   = new Date(end)   - new Date(start);
  const elapsed = Date.now()      - new Date(start);
  if (total <= 0) return 100;
  return Math.min(100, Math.max(0, (elapsed / total) * 100));
}

function toneLabel(progressPct, timePct) {
  const gap = progressPct - timePct;
  if (gap >= -5)  return 'GREEN';
  if (gap >= -20) return 'YELLOW';
  return 'RED';
}

// ─── main export ──────────────────────────────────────────────────────────────

export async function buildAdvisorContext({
  positionId,
  perfCycleId,
  cycleName,
  cycleStart,
  cycleEnd,
}) {
  if (!positionId || !perfCycleId) return '';

  try {
    const [statsRes, worstRes, employeeRes, attentionRes] = await Promise.allSettled([
      GetPerformanceStatsApi(positionId, perfCycleId),
      GetBestWorstTopPlans(positionId, perfCycleId),
      GetEmployeeSummaryApi(positionId, perfCycleId),
      GetAttentionRequiredApi(positionId, perfCycleId, 'plans', 5),
    ]);

    // ── Cycle health ─────────────────────────────────────────────────────────
    const stats    = statsRes.status === 'fulfilled' ? statsRes.value?.data : null;
    const orgPerf  = Number(stats?.organization?.performance  || 0);
    const unitPerf = Number(stats?.unit?.performance          || 0);
    const yourPerf = Number(stats?.individual?.performance    || 0);

    // data[] array: [0]=AtRisk, [1]=OnTrack, [2]=OffTrack (matches carousel usage)
    const orgData  = stats?.organization?.data || [];
    const atRisk   = orgData[0]?.value || 0;
    const onTrack  = orgData[1]?.value || 0;
    const offTrack = orgData[2]?.value || 0;

    const timePct     = timeElapsedPct(cycleStart, cycleEnd);
    const remaining   = daysLeft(cycleEnd);
    const tone        = toneLabel(orgPerf, timePct);

    const totalDays   = cycleStart && cycleEnd
      ? Math.ceil((new Date(cycleEnd) - new Date(cycleStart)) / 86400000)
      : null;
    const elapsedDays = totalDays !== null ? Math.max(0, totalDays - (remaining ?? 0)) : null;

    let cycleBlock = `[CYCLE CONTEXT — ${cycleName || 'Active Cycle'}]\n`;
    if (cycleStart && cycleEnd) {
      const fmt = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
      cycleBlock += `Cycle period: ${fmt(cycleStart)} → ${fmt(cycleEnd)}\n`;
    }
    if (totalDays !== null) {
      cycleBlock += `Time elapsed: ${pct(timePct)} (${elapsedDays} of ${totalDays} days) | `;
    }
    cycleBlock += `Overall progress: ${pct(orgPerf)} | Status: ${tone}\n`;
    cycleBlock += `Organisation: ${pct(orgPerf)} | Unit: ${pct(unitPerf)} | Your plans: ${pct(yourPerf)}\n`;
    cycleBlock += `Plans on track: ${onTrack} | Off track: ${offTrack} | Attention required: ${atRisk}\n`;

    // ── Attention Required plans (from /v1/plans/attention-required) ──────────
    const attData    = attentionRes.status === 'fulfilled' ? attentionRes.value : null;
    const attPlans   = attData?.data?.slice(0, 5) || [];

    let attentionBlock = '';
    if (attPlans.length > 0) {
      attentionBlock = '\n[ATTENTION REQUIRED PLANS — Needs Immediate Focus]\n';
      attPlans.forEach((p, i) => {
        const owner  = ownerName(p.assignedTo || p.positions?.users);
        const perf   = Number(p.performance || p.achievedPercentage || 0);
        const target = p.target || 'N/A';
        const dl     = p.targetDate ? daysLeft(p.targetDate) : null;
        const dlStr  = dl !== null ? (dl === 0 ? 'OVERDUE' : `${dl} days left`) : 'no deadline';
        const stale  = p.daysSinceUpdate ? ` | Stale: ${p.daysSinceUpdate}d` : '';
        attentionBlock += `${i + 1}. "${p.title}" | Owner: ${owner} | Progress: ${pct(perf)} | Target: ${target} | ${dlStr}${stale}\n`;
      });
    }

    // ── Worst performing top plans (from /v1/performance/best-worst-top-plans) ─
    const worstData  = worstRes.status === 'fulfilled' ? worstRes.value : null;
    const worstPlans = worstData?.data?.worstTopPlans?.slice(0, 3) || [];

    let worstBlock = '';
    if (worstPlans.length > 0) {
      worstBlock = '\n[WORST PERFORMING STRATEGIC PLANS — Bottom 3]\n';
      worstPlans.forEach((p, i) => {
        const owner  = ownerName(p.assignedTo);
        const perf   = Number(p.performance || p.achievedPercentage || 0);
        const target = p.target || 'N/A';
        const dl     = p.targetDate ? daysLeft(p.targetDate) : null;
        const dlStr  = dl !== null ? (dl === 0 ? 'OVERDUE' : `${dl} days left`) : 'no deadline';
        worstBlock += `${i + 1}. "${p.title}" | Owner: ${owner} | Progress: ${pct(perf)} | Target: ${target} | ${dlStr}\n`;
      });
    }

    // ── Employee performance tiers ────────────────────────────────────────────
    const empData   = employeeRes.status === 'fulfilled' ? employeeRes.value : null;
    const employees = empData?.data || [];

    const sorted = [...employees].sort((a, b) =>
      Number(b.performance || b.achievedPercentage || 0) -
      Number(a.performance || a.achievedPercentage || 0)
    );

    const top3    = sorted.filter(e => Number(e.performance || e.achievedPercentage || 0) >= 70).slice(0, 3);
    const mid3    = sorted.filter(e => {
      const p = Number(e.performance || e.achievedPercentage || 0);
      return p >= 40 && p < 70;
    }).slice(0, 3);
    const bottom3 = sorted.filter(e => Number(e.performance || e.achievedPercentage || 0) < 40).slice(0, 3);

    function empLine(e) {
      const name = ownerName(e);
      const role = e.designationName || e.designation || '';
      const perf = Number(e.performance || e.achievedPercentage || 0);
      const plan = e.planTitle || '';
      return `${name}${role ? ` (${role})` : ''} — ${pct(perf)}${plan ? ` | ${plan}` : ''}`;
    }

    let employeeBlock = '\n[EMPLOYEE PERFORMANCE TIERS]\n';
    employeeBlock += `On Track (≥70%):     ${top3.length    > 0 ? top3.map(empLine).join(' | ')    : 'None'}\n`;
    employeeBlock += `Off Track (40–69%):  ${mid3.length    > 0 ? mid3.map(empLine).join(' | ')    : 'None'}\n`;
    employeeBlock += `At Risk (<40%):      ${bottom3.length > 0 ? bottom3.map(empLine).join(' | ') : 'None'}\n`;

    return cycleBlock + attentionBlock + worstBlock + employeeBlock;

  } catch (err) {
    console.error('[buildAdvisorContext] error:', err);
    return '';
  }
}
